import ModalContent from 'components/modal/components/ModalContent';
import ModalFooter from 'components/modal/components/ModalFooter';
import ModalTitle from 'components/modal/components/ModalTitle';
import Button from 'components/ui/button';
import ButtonsGroup from 'components/ui/buttonsGroup/ButtonsGroup';
import modes from 'components/ui/buttonsGroup/modes';
import { Card } from 'components/ui/card';
import CustomSelect from 'components/ui/customSelect';
import Form from 'components/ui/form/Form';
import InfoText from 'components/ui/InfoText';
import Input from 'components/ui/input/Input';
import InputAmount from 'components/ui/inputAmount';
import InputDate from 'components/ui/inputDate/InputDate';
import Switch from 'components/ui/switch';
import { useTranslation } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import SelectItem from 'types/SelectItem';
import { HoldbackFormState } from './types';
import { HoldbackData } from 'pages/finance/modules/holdbacks/types';
import DateHelpers from 'helpers/Date';
import dateFormats from 'constants/dateFormats';

const holdbackTypes = [
  {
    label: 'finance.holdback.form.holdbackType.fixAmount.label',
    value: 'fix',
    description: 'finance.holdback.form.holdbackType.fixAmount.desctiption',
  },
  {
    label: 'finance.holdback.form.holdbackType.percent.label',
    value: 'percent',
    description: 'finance.holdback.form.holdbackType.percent.desctiption',
  },
  {
    label: 'finance.holdback.form.holdbackType.threshold.label',
    value: 'threshold',
    description: 'finance.holdback.form.holdbackType.threshold.desctiption',
  },
];

interface HoldbackFormProps {
  isLoading: boolean;
  balances: SelectItem[];
  isEditMode: boolean;
  holdbackData?: HoldbackData;
  onSubmit: (form: HoldbackFormState) => void;
}

export const HoldbackForm = ({
  isLoading,
  onSubmit,
  balances = [],
  isEditMode,
  holdbackData,
}: HoldbackFormProps) => {
  const getInitialForm = (): HoldbackFormState => {
    const todayDateStart = DateHelpers.getFormat(
      DateHelpers.getDate(),
      'noTZCommon'
    );

    if (isEditMode && holdbackData) {
      const matchedBalance =
        balances.find((b) => b.value == holdbackData.balanceId) || null;
      const matchedContract =
        (matchedBalance?.contractConditions as SelectItem[] | undefined)?.find(
          (c) => c.value == holdbackData.contractConditionsId
        ) || null;

      return {
        balance: matchedBalance,
        contractConditions: matchedContract,
        holdFix: holdbackData.holdFix != null ? String(holdbackData.holdFix) : '',
        holdPercent:
          holdbackData.holdPercent != null
            ? String(holdbackData.holdPercent)
            : '',
        dateStart: holdbackData.dateStart || todayDateStart,
        dateEnd: holdbackData.dateEnd || '',
        holdPercentPeriod:
          holdbackData.holdPercentPeriod != null
            ? String(holdbackData.holdPercentPeriod)
            : '',
        comment: holdbackData.comment || '',
        holdType: holdbackData.holdType || holdbackTypes[0].value,
        status: holdbackData.status || 'active',
      };
    }

    return {
      balance: null,
      contractConditions: null,
      holdFix: '',
      holdPercent: '',
      dateStart: todayDateStart,
      dateEnd: '',
      holdPercentPeriod: '',
      comment: '',
      holdType: holdbackTypes[0].value,
      status: 'active',
    };
  };

  const [form, setForm] = useState(() => getInitialForm());

  useEffect(() => {
    if (isEditMode && holdbackData && balances.length > 0) {
      setForm(getInitialForm());
    }
  }, [balances]);

  const getTranslate = useTranslation();

  const changeForm = (key: string, value: string) => {
    setForm((prevForm) => ({
      ...prevForm,
      ...(key === 'holdType' && {
        dateStart: DateHelpers.getFormat(DateHelpers.getDate(), 'noTZCommon'),
      }),
      [key]: value,
    }));
  };

  const changeBalance = (balance: SelectItem | null) => {
    setForm((prevForm) => ({
      ...prevForm,
      balance,
      ...(balance?.value != prevForm.balance?.value && {
        contractConditions: null,
      }),
    }));
  };

  const selectedBalance = useMemo(() => {
    return balances.find((balance) => balance.value == form.balance?.value);
  }, [form.balance, balances]);

  const contractOptions: SelectItem[] =
    selectedBalance?.contractConditions || [];

  const isSubmitDisabled = useMemo(() => {
    if (!form.balance) return true;

    switch (form.holdType) {
      case 'fix':
        return !form.holdFix || !form.dateStart;
      case 'percent':
        return !form.holdPercent || !form.holdPercentPeriod;
      case 'threshold':
        return !form.holdPercent || !form.holdFix;
      default:
        return true;
    }
  }, [form]);

  return (
    <Form
      customClass='ui-form-fields__form flex'
      onSubmit={() => {
        onSubmit(form);
      }}>
      <ModalTitle>
        {getTranslate(
          isEditMode
            ? 'finance.holdback.form.edit.title'
            : 'finance.holdback.form.create.title'
        )}
      </ModalTitle>
      <ModalContent customClass='ui-form-fields__container'>
        <div className='ui-form-fields__item'>
          <CustomSelect
            value={form.balance}
            label={getTranslate('finance.holdback.registry.balanceName.header')}
            options={balances}
            modern
            isLoading={isLoading}
            isSearchable
            isRequired
            onChange={changeBalance}
          />
        </div>
        <div
          className='ui-form-fields__item'
          title={
            typeof form.contractConditions?.label === 'string'
              ? form.contractConditions.label
              : undefined
          }>
          <CustomSelect
            value={form.contractConditions}
            label={getTranslate('finance.holdback.registry.contract.header')}
            options={contractOptions}
            modern
            isLoading={isLoading}
            isSearchable
            isDisabled={!selectedBalance}
            onChange={(value) => changeForm('contractConditions', value)}
          />
        </div>
        <div className='ui-form-fields__item'>
          <ButtonsGroup
            className='min-h-auto'
            label={getTranslate('finance.holdback.form.holdbackType.label')}
            activeButtons={[form.holdType]}
            separate={true}
            mode={modes.singleRequired}
            onClick={(state: string[]) => changeForm('holdType', state[0])}>
            {holdbackTypes.map((holdbackType) => {
              return (
                <Card
                  id={holdbackType.value}
                  title={getTranslate(holdbackType.label)}
                  type='outlined'
                  customClass='basis-full cursor-pointer'>
                  <div className='text-text-secondary'>
                    {getTranslate(holdbackType.description)}
                  </div>
                </Card>
              );
            })}
          </ButtonsGroup>
        </div>

        <div className='ui-form-fields__item max-tablet:flex-col flex items-start gap-4'>
          {(form.holdType === 'percent' || form.holdType === 'threshold') && (
            <div className='ui-form-fields__item'>
              <InputAmount
                id='holdPercent'
                isRequired
                value={form.holdPercent}
                label={getTranslate('finance.holdback.form.percent.label')}
                placeholder=''
                modern
                onChange={(e) => changeForm('holdPercent', e.target.value)}
              />
            </div>
          )}
          {(form.holdType === 'fix' || form.holdType === 'threshold') && (
            <div className='ui-form-fields__item'>
              <InputAmount
                id='holdFix'
                isRequired
                value={form.holdFix}
                label={getTranslate(
                  form.holdType === 'fix'
                    ? 'finance.holdback.form.fixAmount.label'
                    : 'finance.holdback.form.threshold.label'
                )}
                placeholder=''
                modern
                onChange={(e) => changeForm('holdFix', e.target.value)}
              />
            </div>
          )}
          {form.holdType === 'fix' && (
            <div className='ui-form-fields__item'>
              <InputDate
                selectedDate={form.dateEnd}
                title={getTranslate(
                  'finance.holdback.registry.unholdDate.header'
                )}
                modern
                isRequired
                withTime={false}
                closeAfterSelect
                maxDate='none'
                applyFormat={dateFormats.noTZCommon}
                onChange={(value) => changeForm('dateEnd', value)}
              />
            </div>
          )}

          {form.holdType === 'percent' && (
            <div className='ui-form-fields__item'>
              <InputAmount
                id='holdPercentPeriod'
                isRequired
                value={form.holdPercentPeriod}
                label={getTranslate('finance.holdback.form.period.label')}
                placeholder=''
                modern
                suffix={getTranslate('finance.holdback.form.period.suffix')}
                onChange={(e) =>
                  changeForm('holdPercentPeriod', e.target.value)
                }
              />
            </div>
          )}
        </div>

        {form.holdType === 'fix' &&
          form.holdFix &&
          selectedBalance &&
          form.dateEnd && (
            <InfoText
              customClass='flex'
              text={getTranslate(
                'finance.holdback.form.holdbackType.fixAmount.info',
                {
                  amount: form.holdFix ?? '0',
                  currency: selectedBalance.currency ?? '',
                  balanceName: selectedBalance.label ?? '',
                  unholdDate: form.dateEnd ?? '',
                }
              )}
            />
          )}
        {form.holdType === 'percent' &&
          selectedBalance &&
          form.holdPercent &&
          form.holdPercentPeriod && (
            <InfoText
              customClass='flex'
              text={getTranslate(
                'finance.holdback.form.holdbackType.percent.info',
                {
                  percent: form.holdPercent ?? '0',
                  balanceName: selectedBalance.label || '',
                  period: form.holdPercentPeriod,
                }
              )}
            />
          )}
        {form.holdType === 'threshold' && selectedBalance && (
          <InfoText
            customClass='flex'
            text={getTranslate(
              'finance.holdback.form.holdbackType.threshold.info',
              {
                percent: form.holdPercent ?? 0,
                amount: form.holdFix ?? 0,
                currency: selectedBalance?.currency || '',
              }
            )}
          />
        )}

        <div className='ui-form-fields__item'>
          <Card
            title={getTranslate('finance.holdback.registry.status.header')}
            type='outlined'
            status='light'
            floatRight={
              <Switch
                id='status'
                checked={form.status === 'active'}
                onChange={(e) =>
                  changeForm('status', e.target.checked ? 'active' : 'disabled')
                }
              />
            }>
            <div className='text-text-secondary'>
              {getTranslate('finance.holdback.form.status.description')}
            </div>
          </Card>
        </div>
        <div className='ui-form-fields__item'>
          <Input
            id='comment'
            value={form.comment}
            label={getTranslate('finance.holdback.form.comment.label')}
            placeholder={getTranslate(
              'finance.holdback.form.comment.placeholder'
            )}
            modern
            type='textarea'
            onChange={(e) => changeForm('comment', e.target.value)}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          text={getTranslate(
            isEditMode
              ? 'finance.holdback.form.edit.button'
              : 'finance.holdback.form.create.button'
          )}
          status='primary'
          type='submit'
          disabled={isSubmitDisabled}
        />
      </ModalFooter>
    </Form>
  );
};
